import { getCloudUser, serverFeatureAvailable } from "../../features/cloudstorage/cloud";
import { getAppCfg } from "../app-cfg";
import { getDomainCfg, setDomainCfg } from "../state";

// Fixed salt for public user identifier hashing
const USER_ID_SALT = "public-vibed-corn-value";

/**
 * Hash a user identifier (email address or username) for privacy protection
 * Uses SHA-256 hash with salt - Web Crypto API in browser, hash() in PHP
 * @param userIdentifier User identifier (email or username) to hash
 * @returns Promise that resolves to hex hash string with 'UID_' prefix (first 8 characters of SHA-256 hash)
 */
async function hashUserIdentifier(userIdentifier: string): Promise<string> {
  // Normalize input: lowercase and trim (must match PHP: strtolower(trim()))
  const normalized = userIdentifier.toLowerCase().trim();
  
  // Use Web Crypto API for SHA-256 hashing
  const data = new TextEncoder().encode(normalized + "::" + USER_ID_SALT);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  
  // Take first 8 characters for shorter identifier
  const shortHash = hashHex.substring(0, 8);
  
  // Add a prefix 'UID_' to clearly distinguish hashed identifiers from plain identifiers
  return 'UID_' + shortHash;
}

export const createProjectUrl = (projectId : string) : string => {
  const domainCfg = getDomainCfg();
  const projectSettings = domainCfg.projectSettings;

  const projectUrl = getProjectUrl(domainCfg, projectId);
  domainCfg.projectSettings = {
    ...projectSettings,
    projectUrl
  }
  setDomainCfg(domainCfg);
  return projectUrl;
}

export const getProjectUrl = (domainCfg, projectId) => {
  const projectSettings = domainCfg.projectSettings;
  const codeId = domainCfg.viewSettings.codeId;
  const templateProjectId = getTemplateProjectId(projectId.toUpperCase());
  return domainCfg.baseUrl + projectSettings.projectPath + codeId + templateProjectId.projectId;
}


export const createTemplateProjectId = (projectId : string, templateId: string) : string => {
  return projectId + "_" + templateId;
}

export const getTemplateProjectId = (projectId : string) => {
  if (!projectId) {
    return { projectId:"", templateNumber:-1 };
  }
  const underscoreIndex = projectId.indexOf("_");
  if (underscoreIndex < 0) {
    return { projectId, templateNumber:-1 };
  }

  const templateNumber = projectId.substring(underscoreIndex + 1, projectId.length);
  const templateProject = projectId.substring(0, underscoreIndex);
  if (Number.isInteger(parseInt(templateNumber,10))) {
    return { projectId: templateProject, templateNumber: parseInt(templateNumber,10) };
  }

  return { projectId, templateNumber:-1 };
}


/**
 * check if project is saved in cloud for public usage
 * @param {*} id projectId to check
 */
 export const isCloudProjectId = (id : string) => {
  try {
    // Try to decode directly first
    let idString: string;
    try {
      idString = atob(id);
    } catch (e) {
      // If decoding fails, try restoring base64 padding
      const restored = restoreBase64(id);
      try {
        idString = atob(restored);
      } catch (e2) {
        // If still fails, it's not a valid cloud project ID
        return false;
      }
    }
    return (idString.indexOf(":") > -1);
  } catch(error) {
    console.log("no cloud project", id);
  }

  return false;
}

/**
 * check if project is saved in cloud for public usage
 * @param {*} id projectId to check
 * @returns Object with projectId and userId 
 *   - userId starts with "UID_" if it's a hashed identifier (new format for privacy)
 *   - userId is plain email/username if it's an old link (backward compatibility)
 */
 export const getProjectMetaFromCloudId = (id : string) => {
  try {
    // Try to decode directly first
    let idString: string;
    try {
      idString = atob(id);
    } catch (e) {
      // If decoding fails, try restoring base64 padding
      const restored = restoreBase64(id);
      try {
        idString = atob(restored);
      } catch (e2) {
        // If still fails, it's not a valid cloud project ID
        throw e2;
      }
    }
    
    const index = idString.indexOf(":")
    if (index > -1) {
      const userId = idString.substring(0, index);
      const projectId = idString.substring(index + 1, idString.length);
      // Return userId as-is for backward compatibility
      // Check userId.startsWith("UID_") to detect if it's a hashed identifier
      return { projectId, userId };
    }
  } catch(error) {
    console.log("no cloud project", id, error);
  }

  return { projectId : id, userId:"" };
}


export const cloudProjectId = async (projectId? : string) : Promise<string> => {
  let currentProjectId = getAppCfg().projectId;
  if (projectId !== undefined) {
    currentProjectId = projectId;
  }

  const user = getCloudUser();
  if (!serverFeatureAvailable() || !user.loggedIn) {
    return "";
  }

  return await createCloudId(user.userName, currentProjectId);
}

/**
 * Get cloud project ID with backward compatibility support
 * Tries both new format (hashed user) and old format (unhashed user) for loading projects
 * @param projectId The project ID (e.g., "HTML_2")
 * @param publicProject Whether this is a public project (affects how we load it)
 * @returns Promise that resolves to the cloud project ID that exists on the server, or the new format if neither exists
 */
export const cloudProjectIdWithFallback = async (projectId: string, publicProject: boolean = false): Promise<string> => {
  const user = getCloudUser();
  if (!serverFeatureAvailable() || !user.loggedIn) {
    return "";
  }

  // First, try the new format with hashed user identifier
  const newCloudId = await createCloudId(user.userName, projectId);
  
  // Import loadCloudData dynamically to avoid circular dependencies
  const { loadCloudData } = await import("../../features/cloudstorage/wp-json");
  
  // Try loading with new format first (just check if it exists, don't load full data)
  const newFormatResult = await loadCloudData(newCloudId, publicProject);
  if (newFormatResult.success) {
    return newCloudId;
  }

  // If new format doesn't exist (404), try old format with unhashed user identifier
  // Only try old format if user is logged in and has a non-hashed username
  if (newFormatResult.status === 404 && user.userName && typeof user.userName === 'string' && !user.userName.startsWith('UID_')) {
    const oldCloudId = await createCloudId(user.userName, projectId, false); // false = don't hash
    const oldFormatResult = await loadCloudData(oldCloudId, publicProject);
    if (oldFormatResult.success) {
      console.log(`Project ${projectId} found with old format (unhashed user: ${user.userName}), using old format for backward compatibility`);
      return oldCloudId; // Return old format for now, migration can happen later
    }
  }

  // If neither exists, return new format (will be created on save)
  return newCloudId;
}

export const createCloudId = async (userName : string | number | undefined, projectId : string, hashUser: boolean = true) : Promise<string> =>  {
  // For privacy: always hash user identifiers (email addresses or usernames) instead of using them directly
  let userIdToUse: string | number | undefined = userName;
  if (userName && typeof userName === 'string') {
    // If userName already starts with "UID_", it's already a hash (from a shared link)
    // Don't hash it again - use it as-is
    if (userName.startsWith('UID_')) {
      userIdToUse = userName;
    } else if (hashUser) {
      // Always hash string user identifiers for privacy protection
      // This ensures email addresses and usernames are not visible in share links
      userIdToUse = await hashUserIdentifier(userName);
    } else {
      // For backward compatibility: use unhashed user identifier
      userIdToUse = userName;
    }
  }
  
  const base64Id = btoa(`${userIdToUse}:${projectId}`);
  const cleanedUp = cleanUpId(base64Id);
  return cleanedUp;
}

const cleanUpId = (base64String : string) : string => {
  let cleaner = base64String.split("+").join("");
  cleaner = cleaner.split("=").join("");
  return cleaner.split("/").join("");
}

/**
 * Restore base64 string by replacing characters that were removed by cleanUpId
 * This is a best-effort approach to decode cleaned base64 strings
 * @param cleanedId Base64 string with +, =, / removed
 * @returns Restored base64 string (may not be perfect, but should work for most cases)
 */
function restoreBase64(cleanedId: string): string {
  // Base64 uses +, /, and = for padding
  // cleanUpId removes these, so we need to restore them
  // Since we don't know the original positions, we try to restore padding first
  let restored = cleanedId;
  
  // Restore padding (= characters at the end)
  const paddingNeeded = (4 - (restored.length % 4)) % 4;
  restored = restored + '='.repeat(paddingNeeded);
  
  // Note: We cannot perfectly restore + and / characters as we don't know their positions
  // However, most base64 strings don't contain these characters, so this should work for most cases
  // If decoding still fails, the error will be caught and handled gracefully
  
  return restored;
}
